import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { ImageUpload, inputCls } from "@/admin/fields";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function HomeEditor() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/pages/home").then((r) => setC(r.data?.content || {})); }, []);
  const save = async () => { setSaving(true); try { await api.put("/admin/pages/home", { content: c }); toast.success("Saved"); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); } };
  if (!c) return <Loader2 className="mt-8 h-6 w-6 animate-spin text-slate-400" />;
  return (
    <div className="space-y-4">
      <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Hero Headline</label><input className={inputCls} value={c.hero_headline || ""} onChange={(e) => setC({ ...c, hero_headline: e.target.value })} data-testid="page-home-headline" /></div>
      <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Hero Subtext</label><textarea rows={3} className={inputCls} value={c.hero_subtext || ""} onChange={(e) => setC({ ...c, hero_subtext: e.target.value })} /></div>
      <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Hero Image (optional)</label><ImageUpload value={c.hero_image} onChange={(v) => setC({ ...c, hero_image: v })} testid="page-home-hero-image" /></div>
      <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Welcome Section Image</label><ImageUpload value={c.welcome_image} onChange={(v) => setC({ ...c, welcome_image: v })} testid="page-home-welcome-image" /></div>
      <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Player Development Image</label><ImageUpload value={c.development_image} onChange={(v) => setC({ ...c, development_image: v })} testid="page-home-development-image" /></div>
      <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">More Than Rugby Image</label><ImageUpload value={c.more_than_rugby_image} onChange={(v) => setC({ ...c, more_than_rugby_image: v })} testid="page-home-mtr-image" /></div>
      <SaveBtn onClick={save} saving={saving} />
    </div>
  );
}

function AboutEditor() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/pages/about").then((r) => setC(r.data?.content || {})); }, []);
  const save = async () => { setSaving(true); try { await api.put("/admin/pages/about", { content: c }); toast.success("Saved"); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); } };
  if (!c) return <Loader2 className="mt-8 h-6 w-6 animate-spin text-slate-400" />;
  const T = ({ k, label, rows = 3 }) => (<div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">{label}</label><textarea rows={rows} className={inputCls} value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} data-testid={`page-about-${k}`} /></div>);
  return (
    <div className="space-y-4">
      <T k="overview" label="Club Overview" rows={4} />
      <T k="mission" label="Mission" />
      <T k="vision" label="Vision" />
      <T k="philosophy" label="Development Philosophy" />
      <T k="safety" label="Player Safety" />
      <T k="community" label="Community Involvement" />
      <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Club Values (one per line)</label><textarea rows={4} className={inputCls} value={Array.isArray(c.values) ? c.values.join("\n") : ""} onChange={(e) => setC({ ...c, values: e.target.value.split("\n").filter((x) => x.trim()) })} /></div>
      <SaveBtn onClick={save} saving={saving} />
    </div>
  );
}

function JoinEditor() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/pages/join").then((r) => setC(r.data?.content || { faqs: [] })); }, []);
  const save = async () => { setSaving(true); try { await api.put("/admin/pages/join", { content: c }); toast.success("Saved"); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setSaving(false); } };
  if (!c) return <Loader2 className="mt-8 h-6 w-6 animate-spin text-slate-400" />;
  const faqs = c.faqs || [];
  const setFaq = (i, k, v) => { const arr = [...faqs]; arr[i] = { ...arr[i], [k]: v }; setC({ ...c, faqs: arr }); };
  return (
    <div className="space-y-4">
      <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Welcome Message</label><textarea rows={3} className={inputCls} value={c.welcome || ""} onChange={(e) => setC({ ...c, welcome: e.target.value })} /></div>
      <div className="flex items-center justify-between"><label className="text-xs font-bold uppercase text-slate-400">FAQs</label><button onClick={() => setC({ ...c, faqs: [...faqs, { q: "", a: "" }] })} className="inline-flex items-center gap-1 rounded bg-slate-700 px-2 py-1 text-xs font-bold text-white"><Plus className="h-3 w-3" /> Add</button></div>
      {faqs.map((f, i) => (
        <div key={i} className="rounded-lg border border-slate-700 p-3">
          <div className="flex gap-2"><input placeholder="Question" className={inputCls} value={f.q} onChange={(e) => setFaq(i, "q", e.target.value)} /><button onClick={() => setC({ ...c, faqs: faqs.filter((_, j) => j !== i) })} className="rounded bg-slate-700 p-2 hover:bg-red-600"><Trash2 className="h-4 w-4" /></button></div>
          <textarea placeholder="Answer" rows={2} className={`${inputCls} mt-2`} value={f.a} onChange={(e) => setFaq(i, "a", e.target.value)} />
        </div>
      ))}
      <SaveBtn onClick={save} saving={saving} />
    </div>
  );
}

const SaveBtn = ({ onClick, saving }) => (
  <button onClick={onClick} disabled={saving} data-testid="page-save-button" className="inline-flex items-center gap-2 rounded-lg bg-[#E8A317] px-5 py-2 text-sm font-bold uppercase text-[#0A1626]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
);

export default function PagesManager() {
  return (
    <div>
      <h1 className="font-heading text-2xl font-extrabold uppercase text-white">Page Content</h1>
      <p className="text-sm text-slate-400">Edit editable website sections.</p>
      <Tabs defaultValue="home" className="mt-6">
        <TabsList className="bg-[#0C131F]">
          <TabsTrigger value="home" data-testid="page-tab-home">Home</TabsTrigger>
          <TabsTrigger value="about" data-testid="page-tab-about">About</TabsTrigger>
          <TabsTrigger value="join" data-testid="page-tab-join">Join</TabsTrigger>
        </TabsList>
        <div className="mt-4 rounded-xl border border-slate-700 bg-[#151F30] p-5">
          <TabsContent value="home"><HomeEditor /></TabsContent>
          <TabsContent value="about"><AboutEditor /></TabsContent>
          <TabsContent value="join"><JoinEditor /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
